import { ChangeEventHandler, EventHandler, FC, SyntheticEvent, useState } from 'react';
import { Popper } from '@nexelem/deprecated-theme';
import { VirtualKeyboard } from './VirtualKeyboard';
import { useConfig } from '@nexelem/config';

type VirtualKeyboardHandler = {
  onOpen?: EventHandler<SyntheticEvent>;
  onClose?: EventHandler<SyntheticEvent>;
};

export type VirtualKeyboardPopperEnable = 'full' | 'numeric' | 'off';

type VirtualKeyboardPopperProps = {
  children: FC<VirtualKeyboardHandler>;
  value: string;
  onChange?: ChangeEventHandler<HTMLInputElement | HTMLTextAreaElement>;
  enable?: VirtualKeyboardPopperEnable;
  syncCaret?: boolean;
  maxLength?: number;
} & Partial<VirtualKeyboardHandler>;

export const VirtualKeyboardPopper = ({
  children,
  onClose,
  onOpen,
  onChange,
  value,
  enable = 'off',
  syncCaret,
  maxLength,
}: VirtualKeyboardPopperProps) => {
  const { modules } = useConfig();
  const [resetIndex, setResetIndex] = useState(0);
  const [anchorEl, setAnchorEl] = useState<null | Element>(null);
  const keyboardOpen = Boolean(anchorEl);

  const handleClick = (event: SyntheticEvent) => {
    setAnchorEl(anchorEl ? null : event.currentTarget);
    onOpen?.(event);
  };

  const handleClose = (event: SyntheticEvent) => {
    setAnchorEl(null);
    onClose?.(event);
  };

  const closeKeyboard = () => {
    setAnchorEl(null);
    (window.document.activeElement as HTMLInputElement | null)?.blur();
  };

  // update popper position on virtual keyboard layout change
  const resetPosition = () => {
    setResetIndex(resetIndex + 1);
  };

  if (!modules.global.virtualKeyboard) {
    return <>{children({ onClose, onOpen })}</>;
  }

  return (
    <>
      {children({ onClose: handleClose, onOpen: handleClick })}
      {enable !== 'off' && (
        <Popper
          anchorEl={anchorEl}
          className="z-1400"
          id={'virtual-keyboard-popper' + resetIndex}
          open={keyboardOpen}
          placement="top"
        >
          <div className="mx-8 mb-2 rounded-xs border border-border-primary bg-surface-container-lighter p-2 shadow-medium">
            <VirtualKeyboard
              defaultLayout={enable}
              maxLength={maxLength}
              syncCaret={syncCaret}
              value={value}
              onChange={onChange}
              onClose={closeKeyboard}
              onLayoutChange={resetPosition}
            />
          </div>
        </Popper>
      )}
    </>
  );
};
